export const caseStudies = [
    {
        id: 'abramson-labor-group',
        title: 'Abramson Labor Group',
        slug: 'abramson-labor-group',
        category: 'AI Implementation & CyberSecurity',
        client: 'Abramson Labor Group',
        industry: 'Legal Services',
        description: 'We implemented AI workflows into Abramson Labor Group\'s production systems, starting with a custom case evaluator and intake platform that instantly qualifies potential clients and lets them sign their retainer without ever leaving the site. The platform learns which cases yield the most success and tracks everything from Cost Per Acquisition (CPA) through to Return On Investment (ROI). Alongside it, we run the firm\'s cybersecurity: ongoing model assessments, a hardened defensive posture, and active monitoring that mitigates nation-state threats and thwarts thousands of real attack attempts every week.',
        liveUrl: 'https://abramsonlaborgroup.com/consultation',
        image: '/images/case-studies/abramson-labor-group.png',
        heroImage: '/images/case-studies/abramson-labor-group.png',
        challenge: {
            title: 'The Challenge',
            description: 'Abramson Labor Group needed a way to efficiently qualify potential clients and streamline their intake process. Their manual system was time-consuming, inconsistent, and couldn\'t scale with their growing caseload. They also lacked visibility into which types of cases were most successful and profitable.',
            points: [
                'Manual intake process was slow and inconsistent',
                'No automated client qualification system',
                'Difficulty tracking case success rates and ROI',
                'High cost per acquisition with low conversion rates',
                'Limited data insights for decision-making',
            ],
        },
        solution: {
            title: 'Our Solution',
            description: 'We developed a comprehensive web application with an intelligent case evaluator that uses machine learning to automatically qualify potential clients. The system includes a seamless intake process, automated retainer signing, and comprehensive analytics dashboard.',
            technologies: [
                'React & TypeScript',
                'Node.js & Express',
                'PostgreSQL',
                'Machine Learning Algorithms',
                'Document Signing Integration',
                'Analytics & Reporting',
            ],
            features: [
                'Automated case evaluation and qualification',
                'Machine learning-powered success prediction',
                'Integrated retainer signing workflow',
                'Real-time analytics dashboard',
                'CPA and ROI tracking',
                'Client data management system',
                'Automated follow-up workflows',
            ],
        },
        results: {
            title: 'Results & Impact',
            metrics: [
                {
                    label: 'Reduction in Intake Time',
                    value: '75%',
                    description: 'From hours to minutes per case',
                },
                {
                    label: 'Increase in Qualified Leads',
                    value: '3.2x',
                    description: 'Better targeting through ML algorithms',
                },
                {
                    label: 'Cost Per Acquisition',
                    value: '-60%',
                    description: 'Significant reduction in marketing costs',
                },
                {
                    label: 'Conversion Rate',
                    value: '+45%',
                    description: 'Higher quality leads convert better',
                },
                {
                    label: 'ROI Improvement',
                    value: '+180%',
                    description: 'Better case selection and tracking',
                },
            ],
            highlights: [
                'Streamlined intake process reduced administrative overhead by 70%',
                'Machine learning model improved case success prediction accuracy to 87%',
                'Automated retainer signing increased completion rate by 92%',
                'Real-time analytics enabled data-driven decision making',
                'System handles 10x more intake volume without additional staff',
            ],
        },
        security: {
            title: 'Security Analysis & Threat Protection',
            description: 'A legal intake platform holds some of the most sensitive data a business can collect: personal details, employment histories, and signed retainers. We treated security as a core feature from day one, with continuous assessment of the platform and its machine learning models, a hardened defensive posture, and active monitoring that stops real attacks every week.',
            metrics: [
                {
                    label: 'Attacks Thwarted Weekly',
                    value: '1,000s',
                    description: 'Real attack attempts detected and blocked every week',
                },
                {
                    label: 'Active Monitoring',
                    value: '24/7',
                    description: 'Continuous detection and alerting across the platform',
                },
                {
                    label: 'Nation-State Threats',
                    value: 'Mitigated',
                    description: 'Defenses built to withstand advanced, state-sponsored actors',
                },
            ],
            pillars: [
                {
                    title: 'Model Assessments',
                    description: 'The case evaluator\'s machine learning models are assessed as an attack surface, not just a feature.',
                    points: [
                        'Adversarial input testing against the case evaluation models',
                        'Safeguards against data poisoning of training and feedback data',
                        'Output validation so manipulated answers cannot skew qualification',
                        'Regular model reviews as new data and attack techniques emerge',
                    ],
                },
                {
                    title: 'Defensive Posture',
                    description: 'Layered defenses protect client data at every point from the intake form to the signed retainer.',
                    points: [
                        'Encryption of sensitive client data in transit and at rest',
                        'Least-privilege access controls across systems and staff',
                        'Hardened infrastructure with minimal exposed surface area',
                        'Secure handling of documents and retainer signatures',
                    ],
                },
                {
                    title: 'Active Cybersecurity Monitoring',
                    description: 'Traffic and system activity are watched continuously, so threats are caught as they happen.',
                    points: [
                        'Real-time detection of suspicious traffic and behavior',
                        'Automated alerting with rapid incident response',
                        'Ongoing log analysis to surface emerging attack patterns',
                        'Regular review of defenses against the latest threat intelligence',
                    ],
                },
                {
                    title: 'Threat Protection',
                    description: 'The platform faces real, persistent attacks, including from nation-state actors, and stops them.',
                    points: [
                        'Mitigation of attacks attributed to nation-state threat actors',
                        'Thousands of real attack vectors thwarted in a typical week',
                        'Protection against bots, credential stuffing, and injection attacks',
                        'Spam and fraudulent submissions filtered before they reach the firm',
                    ],
                },
            ],
        },
        testimonial: {
            quote: 'BuzBiz transformed our intake process completely. What used to take hours now takes minutes, and we\'re seeing better cases and higher success rates. The ROI tracking alone has been invaluable.',
            author: 'Abramson Labor Group',
            role: 'Legal Practice',
        },
        timeline: '4 years',
    },
];
export const getCaseStudyBySlug = (slug) => {
    return caseStudies.find((study) => study.slug === slug);
};
export const getAllCaseStudySlugs = () => {
    return caseStudies.map((study) => study.slug);
};
